/**
 * PaymentAccountTable Component
 * Table displaying payment accounts
 */

import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { PaymentAccount } from "../../../store/api/paymentAccountsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface PaymentAccountTableProps {
	accounts: PaymentAccount[];
	isLoading: boolean;
	onEdit: (account: PaymentAccount) => void;
	onDelete: (account: PaymentAccount) => void;
}

const PaymentAccountTable: React.FC<PaymentAccountTableProps> = ({
	accounts,
	isLoading,
	onEdit,
	onDelete,
}) => {
	// Using centralized formatDateTime utility

	if (isLoading) {
		return (
			<div className="bg-card border border-border rounded-lg p-12">
				<div className="flex items-center justify-center">
					<Icon
						name="Loader2"
						size={32}
						className="animate-spin text-primary"
					/>
				</div>
			</div>
		);
	}

	if (accounts.length === 0) {
		return (
			<div className="bg-card border border-border rounded-lg p-12 text-center">
				<Icon
					name="CreditCard"
					size={48}
					className="mx-auto mb-4 text-muted-foreground opacity-50"
				/>
				<h3 className="text-lg font-semibold text-foreground mb-2">
					No Payment Accounts
				</h3>
				<p className="text-muted-foreground">
					Get started by adding your first payment account.
				</p>
			</div>
		);
	}

	return (
		<div className="bg-card border border-border rounded-lg overflow-hidden">
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
								Account Name
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
								Account Number
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
								Bank Name
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
								Status
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
								Created
							</th>
							<th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-border">
						{accounts.map((account) => (
							<tr
								key={account.id}
								className="hover:bg-muted/30 smooth-transition"
							>
								<td className="px-4 py-4">
									<p className="text-sm font-medium text-foreground">
										{account.account_name}
									</p>
									{account.notes && (
										<p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
											{account.notes}
										</p>
									)}
								</td>
								<td className="px-4 py-4">
									<p className="text-sm text-foreground font-mono">
										{account.account_number}
									</p>
								</td>
								<td className="px-4 py-4">
									<p className="text-sm text-foreground">{account.bank_name}</p>
								</td>
								<td className="px-4 py-4">
									<span
										className={`px-2 py-1 rounded-full text-xs font-medium ${
											account.is_active
												? "bg-success/10 text-success"
												: "bg-muted text-muted-foreground"
										}`}
									>
										{account.is_active ? "Active" : "Inactive"}
									</span>
								</td>
								<td className="px-4 py-4">
									<p className="text-sm text-muted-foreground">
										{formatDateTime(account.created_at)}
									</p>
								</td>
								<td className="px-4 py-4">
									<div className="flex items-center justify-end space-x-2">
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onEdit(account)}
											title="Edit"
										>
											<Icon name="Edit" size={16} />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onDelete(account)}
											title="Delete"
										>
											<Icon name="Trash2" size={16} className="text-error" />
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default PaymentAccountTable;
